import { ingredientPriceCents, MAX_PORTIONS, type BuilderIngredient } from './pizza-builder'

/**
 * Extra toppings on a pizza **off the menu** — `FRONTEND.md` §9.41.
 *
 * Not the custom-pizza builder (§9.25), which composes a pizza from nothing. This is the smaller and
 * far more common thing: a *Margherita* from the catalogue, with ham on it.
 *
 * **Moved here from `manager-orders` on 2026-09-03**, unchanged, so the customer app can use it too:
 * §9.168 gives the customer menu the same dialog and neither app may import the other's module. The
 * manager keeps a re-export at the old path, so nothing it owns had to move with it.
 *
 * Ported from legacy `pizza-extras-modal.tsx`, and its text format is kept to the character because
 * **legacy re-reads its own string** to reopen the dialog with the right quantities. Every order in
 * the dumps carries that format, so a tidier one would mean an order taken last week could not be
 * reopened.
 */

/**
 * How many portions of one topping: one up to {@link MAX_PORTIONS} - `FRONTEND.md` §9.703, FB-1009.
 *
 * Legacy cycles 0 -> 1 -> 2 -> 0 and this was `1 | 2`. §9.678 took the custom pizza to three and left this dialog
 * at two, so a pizza edited in the košík could not have the third portion the builder beside it offers - O82's
 * *"not working in nová objednávka when editing pizza in košík"*. Written out as a union so a fourth is a type
 * error here rather than a silent widening; the test holds it equal to `MAX_PORTIONS`.
 */
export type ExtraQty = 1 | 2 | 3

export type ChosenExtras = ReadonlyMap<number, ExtraQty>

/**
 * What a menu pizza's changed base is written as - `FRONTEND.md` §9.731, owner O100.
 *
 * The base is not a topping and is not charged, so it is not one of the line's `extras`: it is the first piece of
 * the bracket the extras dialog already writes, `Margherita – 32 cm [Základ: Smotanový základ, +Šunka]`, which the
 * receipt, the delivery note and the kitchen read as text. **A literal rather than built from
 * `PIZZA_BASE_CATEGORY`**, because the server spells it too (`MenuPizzaBase.PREFIX`) and the test holding the two
 * together reads this line.
 */
export const MENU_PIZZA_BASE_PREFIX = 'Základ:'

/**
 * One tap adds a portion, each further tap another, and the tap after {@link MAX_PORTIONS} takes the topping off -
 * the builder's own cycle (§9.678), read from its constant rather than from a second cap. A double portion of ham
 * is an ordinary order, so this is not a checkbox.
 */
export function cycleExtra(chosen: ChosenExtras, id: number): Map<number, ExtraQty> {
  const next = new Map(chosen)
  const current = next.get(id) ?? 0
  if (current >= MAX_PORTIONS) next.delete(id)
  else next.set(id, (current + 1) as ExtraQty)
  return next
}

/**
 * The line's topping text — `+Šunka, Niva 2×`.
 *
 * In the ingredients' own order rather than the order they were tapped, so the same pizza reads the
 * same way twice and a kitchen ticket does not depend on which topping somebody pressed first.
 *
 * **The count behind the name for any count above one - §9.703.** This wrote `2×` for exactly two, so a third
 * portion would have printed `+Syr` - one. Count-last is this bracket's own spelling (§9.678 kept it), and the
 * kitchen's `countedPiece` and the server's `extrasSuffix` both read any digit there.
 */
export function extrasText(
  chosen: ChosenExtras,
  ingredients: readonly BuilderIngredient[],
): string {
  return ingredients
    .filter((ingredient) => chosen.has(ingredient.id))
    .map((ingredient) => {
      const qty = chosen.get(ingredient.id) ?? 1
      return qty > 1 ? `${ingredient.name} ${qty}×` : `+${ingredient.name}`
    })
    .join(', ')
}

/** What the toppings add, at this pizza's size. A double portion costs twice. */
export function extrasCents(
  chosen: ChosenExtras,
  ingredients: readonly BuilderIngredient[],
  sizeCm: number,
): number {
  let total = 0
  for (const ingredient of ingredients) {
    const qty = chosen.get(ingredient.id)
    if (qty === undefined) continue
    total += ingredientPriceCents(ingredient, sizeCm) * qty
  }
  return total
}

/**
 * Reads a line's existing text back into quantities, so reopening shows what was chosen.
 *
 * **Every count up to {@link MAX_PORTIONS} - §9.703.** This knew `2×` alone, so a stored `Syr 3×` reopened as
 * **nothing**: the topping fell off the line on the next save.
 *
 * **Whole pieces, not substrings - `FRONTEND.md` §9.730, owner O100.** This was legacy's parse, `text.includes`,
 * so `+Šunka pikantná` chose *Šunka* as well whenever both are on offer. Harmless while the result only lit chips
 * in a dialog nobody re-saved; §9.730 sends what it reads as the line's `extras` on a saved pizza, and the server
 * would charge the second topping. So the text is read the way the server reads the same bracket
 * (`StockDeduction.extras`, `BACKEND.md` §10A.378): split on `,`, the `+` and a count on either side taken off,
 * the name compared whole with case and spaces folded (`Units.normalize`'s fold). A piece naming no ingredient on
 * offer is skipped - which is also what keeps a base changed at the till ({@link MENU_PIZZA_BASE_PREFIX}) out of the
 * toppings: its piece reads `Základ: …`, and compared whole that names no ingredient.
 */
export function parseExtras(
  text: string | undefined,
  ingredients: readonly BuilderIngredient[],
): Map<number, ExtraQty> {
  const chosen = new Map<number, ExtraQty>()
  if (!text) return chosen
  const byName = new Map<string, number>()
  for (const ingredient of ingredients) {
    if (typeof ingredient.name !== 'string') continue
    const key = foldedName(ingredient.name)
    if (!byName.has(key)) byName.set(key, ingredient.id)
  }
  for (const raw of text.split(',')) {
    const piece = raw.trim()
    if (piece === '') continue
    const { name, count } = countedTopping(piece.replace(/^\+\s*/, ''))
    const id = byName.get(foldedName(name))
    if (id !== undefined) chosen.set(id, count)
  }
  return chosen
}

/** A name as two readers compare it - lower case, trimmed, runs of spaces as one; the server's `Units.normalize`. */
function foldedName(name: string): string {
  return name.toLowerCase().trim().replace(/\s+/g, ' ')
}

/** A count before the name, with the multiplication sign - `StockDeduction.LEADING_COUNT`. */
const LEADING_COUNT = /^(\d+)\s*×\s*/

/** A count after the name - `StockDeduction.TRAILING_COUNT`. */
const TRAILING_COUNT = /\s+(\d+)\s*×$/

/**
 * `Šunka 2×` and `2× Šunka` are two portions of Šunka, clamped to one up to {@link MAX_PORTIONS}.
 *
 * The server's own reading, step for step (`StockDeduction.withoutPortionCount` and `portionsOf`), so the two cannot
 * part on a piece neither writes: both counts come off the name, the leading one is the count when there are two, and
 * a count too long to be a number is the cap.
 */
function countedTopping(piece: string): { name: string; count: ExtraQty } {
  const trimmed = piece.trim()
  const name = trimmed.replace(LEADING_COUNT, '').replace(TRAILING_COUNT, '').trim()
  const written = LEADING_COUNT.exec(trimmed)?.[1] ?? TRAILING_COUNT.exec(trimmed)?.[1]
  if (written === undefined) return { name, count: 1 }
  const parsed = Number(written)
  const count = Number.isSafeInteger(parsed) ? Math.min(MAX_PORTIONS, Math.max(1, parsed)) : MAX_PORTIONS
  return { name, count: count as ExtraQty }
}

/**
 * Whether a catalogue line takes toppings.
 *
 * By category name, which is what legacy and the customer app (`HomePage.tsx:184`) both do. A
 * franchise renaming *Pizza* to *Pizze* keeps working; one calling it something with no *pizza* in it
 * would not, and that is the same limit the customer menu already lives with rather than a new one.
 */
export function isPizzaCategory(categoryName: string | undefined | null): boolean {
  return typeof categoryName === 'string' && categoryName.toLowerCase().includes('pizza')
}

/**
 * Whether a catalogue product is a pizza, for the till - `FRONTEND.md` §9.730, owner O100.
 *
 * **Its type or its category's name**, the rule the server already reads a pizza's extras by
 * (`StockDeduction.takesExtras`, and the edit's recovery in `BACKEND.md` §10A.378). The category alone missed a
 * pizza filed somewhere else - Nesvady's *Kačacia pizza* sits in *Jesenné menu*, measured on the 2026-09-26 dump -
 * so the till offered it no toppings while the server would have charged and deducted them. The till's add path
 * and a saved order's line both ask this, so a pizza is one the same way before and after it is saved; the
 * customer app still asks {@link isPizzaCategory}.
 */
export function isPizzaProduct(
  product: { productType?: string | null; categoryName?: string | null } | null | undefined,
): boolean {
  if (product == null) return false
  return product.productType === 'pizza' || isPizzaCategory(product.categoryName)
}

/** The base a line's bracket text names, or null - §9.731. The first base piece; an empty one names nothing. */
export function menuPizzaBaseOf(text: string | null | undefined): string | null {
  if (!text) return null
  for (const raw of text.split(',')) {
    const piece = raw.trim()
    if (!piece.startsWith(MENU_PIZZA_BASE_PREFIX)) continue
    const name = piece.slice(MENU_PIZZA_BASE_PREFIX.length).trim()
    return name === '' ? null : name
  }
  return null
}

/**
 * The bracket text with a base changed at the till first - §9.731. `''` when there is neither, so a pizza with no
 * change keeps no bracket at all.
 */
export function withMenuPizzaBase(extras: string, base: string | null): string {
  const named = base === null ? '' : base.trim()
  const piece = named === '' ? '' : `${MENU_PIZZA_BASE_PREFIX} ${named}`
  return [piece, extras.trim()].filter((part) => part !== '').join(', ')
}
